import MetaRobots from 'components/Basic/Head/MetaRobots';
import { getEndCursor } from 'components/Blocks/Pagination/paginationHelpers';
import Breadcrumbs from 'components/Layout/Breadcrumbs/Breadcrumbs';
import CommonLayout from 'components/Layout/CommonLayout';
import Webline from 'components/Layout/Webline/Webline';
import CategoryDetail from 'components/Pages/CategoryDetail/CategoryDetail';
import {
    useCategoryDetailData,
    useHandleDefaultFiltersUpdate,
} from 'components/Pages/CategoryDetail/categoryDetailUtils';
import { DEFAULT_PAGE_SIZE } from 'config/constants';
import { mapCategoryDetailData } from 'connectors/categories/Categories';
import {
    CategoryDetailQueryApi,
    CategoryDetailQueryDocumentApi,
    CategoryDetailQueryVariablesApi,
    CategoryProductsQueryApi,
    CategoryProductsQueryDocumentApi,
    CategoryProductsQueryVariablesApi,
} from 'graphql/generated';
import { createClient } from 'helpers/createClient';
import { getMappedProductFilter } from 'helpers/filterOptions/getMappedProductFilter';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getIsRedirectedFromSsr } from 'helpers/isServer';
import { getRedirectWithOffsetPage } from 'helpers/loadMore/getRedirectWithOffsetPage';
import { getProductListSortFromUrlQuery } from 'helpers/parsing/urlParsing';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import {
    FILTER_QUERY_PARAMETER_NAME,
    LOAD_MORE_QUERY_PARAMETER_NAME,
    PAGE_QUERY_PARAMETER_NAME,
    SORT_QUERY_PARAMETER_NAME,
} from 'helpers/queryParams/queryParamNames';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useCurrentSortQuery } from 'helpers/queryParams/useCurrentSortQuery';
import { useSeoTitleWithPagination } from 'helpers/seo/useSeoTitleWithPagination';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { handleServerSideErrorResponseForFriendlyUrls } from 'helpers/serverSide/handleServerSideErrorResponseForFriendlyUrls';
import { useGtmFriendlyPageView } from 'hooks/gtm/useGtmFriendlyPageView';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { NextPageExtended } from 'types/global';
import { getNumberFromUrlQuery } from 'utils/getNumberFromUrlQuery';
import { getSlugFromServerSideUrl } from 'utils/getSlugFromUrl';
import { useGtmPageViewEvent } from 'utils/Gtm/EventFactories';
import { getGtmPageInfoForFriendlyUrl } from 'utils/Gtm/Gtm';

const CategoryDetailPage: NextPageExtended<ServerSidePropsType> = () => {
    const router = useRouter();
    const slug = getUrlWithoutGetParameters(router.asPath);
    const currentDomainConfig = useDomainConfig();
    const currentFilter = useCurrentFilterQuery();
    const currentSort = useCurrentSortQuery();
    const { categoryData, isFetchingVisible } = useCategoryDetailData(currentFilter);

    useHandleDefaultFiltersUpdate(categoryData?.products);

    const mappedCategory = useMemo(() => {
        if (categoryData) {
            return mapCategoryDetailData(categoryData, currentDomainConfig.currencyCode);
        }

        return null;
    }, [categoryData, currentDomainConfig.currencyCode]);

    const seoTitle = useSeoTitleWithPagination(
        mappedCategory?.productConnection.totalCount,
        mappedCategory?.name,
        mappedCategory?.seoTitle,
    );

    const gtmCategoryUrlPageViewEvent = useGtmPageViewEvent(
        getGtmPageInfoForFriendlyUrl(mappedCategory, slug, mappedCategory?.breadcrumb),
    );
    useGtmFriendlyPageView(gtmCategoryUrlPageViewEvent, slug, isFetchingVisible);

    const categoryMainImage = mappedCategory?.image ?? undefined;

    return (
        <CommonLayout
            showLastVisitedProducts
            isFetchingData={isFetchingVisible}
            title={seoTitle}
            description={mappedCategory?.seoMetaDescription}
            ogImageUrlDefault={categoryMainImage}
        >
            {(!!currentFilter || !!currentSort) && <MetaRobots content="noindex, follow" />}
            {mappedCategory?.breadcrumb && !isFetchingVisible && (
                <Webline>
                    <Breadcrumbs type="category" key="breadcrumb" breadcrumb={mappedCategory.breadcrumb} />
                </Webline>
            )}
            {mappedCategory && <CategoryDetail category={mappedCategory} />}
        </CommonLayout>
    );
};

CategoryDetailPage.layout = 'plain';

export const getServerSideProps = getServerSidePropsWrapper(
    ({ ssrExchange, domainConfig, redisClient, t }) =>
        async (context) => {
            const client = createClient({
                context,
                ssrExchange,
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
                t,
                redisClient,
            });
            const orderingMode = getProductListSortFromUrlQuery(context.query[SORT_QUERY_PARAMETER_NAME]);
            const filter = getMappedProductFilter(context.query[FILTER_QUERY_PARAMETER_NAME]);
            const page = getNumberFromUrlQuery(context.query[PAGE_QUERY_PARAMETER_NAME], 1);
            const loadMore = getNumberFromUrlQuery(context.query[LOAD_MORE_QUERY_PARAMETER_NAME], 0);
            const urlSlug = getSlugFromServerSideUrl(context.req.url ?? '', context.req.headers);
            const redirect = getRedirectWithOffsetPage(page, loadMore, urlSlug, context.query);

            if (redirect) {
                return redirect;
            }

            const categoryDetailResponsePromise = client
                .query<CategoryDetailQueryApi, CategoryDetailQueryVariablesApi>(CategoryDetailQueryDocumentApi, {
                    urlSlug,
                    filter,
                    orderingMode,
                })
                .toPromise();
            const categoryProductsResponsePromise = client
                .query<CategoryProductsQueryApi, CategoryProductsQueryVariablesApi>(CategoryProductsQueryDocumentApi, {
                    endCursor: getEndCursor(page),
                    orderingMode,
                    filter,
                    urlSlug,
                    first: DEFAULT_PAGE_SIZE * (loadMore + 1),
                })
                .toPromise();

            const [categoryDetailResponse] = await Promise.all([
                categoryDetailResponsePromise,
                categoryProductsResponsePromise,
            ]);

            if (getIsRedirectedFromSsr(context.req.headers)) {
                const serverSideErrorResponse = handleServerSideErrorResponseForFriendlyUrls(
                    categoryDetailResponse.error?.graphQLErrors,
                    categoryDetailResponse.data?.category,
                    context.res,
                );
                if (serverSideErrorResponse) {
                    return serverSideErrorResponse;
                }
            }

            const initServerSideData = await initServerSideProps({ client, context, domainConfig, ssrExchange });

            return initServerSideData;
        },
);
export default CategoryDetailPage;
